import {ChangeDetectorRef, Component, Input, ViewEncapsulation} from '@angular/core';
import {ConfigColumn} from "../../_typing/config/config-column";
import {ConstantProperty} from "../../_models/constant-property";
import {BaseComponent} from "../../shared/base.component";
import {Observable, of} from "rxjs";
import {ConstantPropertyDataService} from "../../data/constant-property-data.service";
import {take, tap} from "rxjs/operators";
import {ConstantFieldComponent} from "../constant-field/constant-field.component";
import {CommonModule} from "@angular/common";
import {SingleResponse} from "../../services/api/response-types";
import {ComponentType} from "../../_models/component-type";
import {IDMap, ModelID} from "../../_typing/generic-types";
import {ComponentTypeDataService} from "../../data/component-type-data.service";
import {ComponentDataService} from "../../data/component-data.service";
import {SelectSearchComponentComponent} from "../select-search-api/single/select-search-component.component";
import {SelectManySearchBaseComponent} from "../select-search-api/many/select-many-search-base.component";
import {DefaultValueService} from "./default-value.service";
import {ConfigStub} from "../../_typing/config-stub";
import {MatCheckbox} from "@angular/material/checkbox";
import {SharedModule} from "../../shared/modules/shared-module.module";

@Component({
    selector: 'default-value-field',
    templateUrl: './default-value-field.component.html',
    styleUrls: ['./default-value-field.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [DefaultValueService],
    imports: [
        CommonModule,
        ConstantFieldComponent,
        ConstantFieldComponent,
        SelectSearchComponentComponent,
        SelectManySearchBaseComponent,
        MatCheckbox,
        SharedModule
    ]
})
export class DefaultValueFieldComponent extends BaseComponent {
    @Input() defaultField: ConfigColumn;
    @Input() constantProperty: ConstantProperty;
    @Input() componentType: ComponentType;
    @Input() firstComponentType: ComponentType;
    placeholder = 'Enter a default value'
    allowValueEntry: boolean = false;
    isUserField: boolean = false;
    useCurrentUser: boolean = false;
    isAccountField: boolean = false;
    useCurrentAccount: boolean = false;

    componentFilters: any[];
    ctCtDict: IDMap<{ isFirstComponent: boolean; isUnique: boolean; isListUnique: boolean }> = {};

    constructor(private propertyData: ConstantPropertyDataService,
                private componentData: ComponentDataService,
                private componentTypeData: ComponentTypeDataService,
                private cdr: ChangeDetectorRef,
                private defaultValueService: DefaultValueService) {
        super();
    }

    ngOnInit() {
        // Will possibly allow attributes in future
        if (this.defaultField?.type === 'constant_property') {
            this._getProperty();
        } else if (this.defaultField?.type === 'component_type' && this.firstComponentType) {
            this._getComponentTypeComponentTypes();
        }
    }

    private _getProperty() {
        let $constantProperty: Observable<SingleResponse<ConstantProperty> | null>;
        if (!this.constantProperty) {
            $constantProperty = this.propertyData.getPropertyLightById(this.defaultField.id)
                .pipe(tap(result => {
                    this.constantProperty = result?.data;
                }))
        } else {
            $constantProperty = of(null);
        }
        $constantProperty.pipe(take(1))
            .subscribe(() => this.setupPage());
    }

    private _getComponentTypeComponentTypes(): void {
        this.defaultValueService.cleanDefaultComponents(this.defaultField).subscribe((defaultValue => {
            this.defaultField.default_value = defaultValue;
        }))

        const relatedCtIds = [this.componentType?.id || this.defaultField.id];
        this.componentTypeData.getComponentTypeComponentTypeRelationships(this.firstComponentType.id, relatedCtIds)
            .pipe(tap(result => {
                this.ctCtDict = result;
                relatedCtIds.forEach(ctId => {
                    this._getComponentTypeComponentFilters(ctId);
                })
            })).subscribe(() => this.setupPage());
    }

    private _getComponentTypeComponentFilters(relatedCtId: ModelID) {
        this.componentFilters = this.componentData.getComponentTypeComponentFilters(this.componentType?.id, relatedCtId, undefined, this.ctCtDict);
    }

    setupPage() {
        if (this.defaultField?.type === 'constant_property') {
            if (!['datetime', 'file'].includes(this.constantProperty?.attributes.data_type) && !this.constantProperty?.attributes.is_calculation) {
                this.allowValueEntry = true;
            }
            if (this.constantProperty?.attributes.data_type === 'user') {
                this.isUserField = true;
            }
            if (this.constantProperty?.attributes.data_type === 'account') {
                this.isAccountField = true;
            }
            if (this.constantProperty?.attributes.is_drop_down_list) {
                this.placeholder = 'Select a default value';
            }
        }
        if (this.defaultField.default_value === 'currentUser') {
            this.useCurrentUser = true;
        }
        if (this.defaultField.default_value === 'currentAccount') {
            this.useCurrentAccount = true;
        }
        if (!this.allowValueEntry) {
            this.placeholder = 'Defaults not allowed';
        }
        this.cdr.markForCheck();
    }

    onUseCurrentUserChange(checked: boolean) {
        this.useCurrentUser = checked;
        if (checked) {
            this.defaultField.default_value = 'currentUser';
        } else if (this.defaultField.default_value === 'currentUser') {
            this.defaultField.default_value = null;
        }
    }

    onUseCurrentAccountChange(checked: boolean) {
        this.useCurrentAccount = checked;
        if (checked) {
            this.defaultField.default_value = 'currentAccount';
        } else if (this.defaultField.default_value === 'currentAccount') {
            this.defaultField.default_value = null;
        }
    }

    onComponentChanged($event) {
        if (Array.isArray($event)) {
            this.defaultField.default_value = ($event || []).map(component => new ConfigStub(component));
        } else {
            this.defaultField.default_value = $event?.value ? new ConfigStub($event.value) : null;
        }
    }
}
